<template>
  <div class="p-4 md:p-6 max-w-3xl mx-auto">
    <button type="button" class="text-xs font-bold text-[#0d4169] mb-4" @click="$router.push({ name: 'jobs' })">
      ← Back to jobs
    </button>

    <div v-if="loading" class="flex justify-center py-16">
      <div class="h-8 w-8 border-4 border-gray-300 border-t-[#0d4169] rounded-full animate-spin"></div>
    </div>

    <p v-else-if="error" class="text-rose-600 text-sm">{{ error }}</p>

    <div v-else-if="job" class="card-panel p-5 md:p-6 space-y-4 text-left">
      <div class="flex flex-wrap items-center justify-between gap-2">
        <h1 class="text-2xl font-extrabold text-slate-800">{{ job.title }}</h1>
        <span class="inline-flex px-2 py-0.5 rounded-full text-[11px] font-bold uppercase"
          :class="{
            'bg-amber-100 text-amber-800': job.status === 'pending',
            'bg-emerald-100 text-emerald-800': job.status === 'published',
            'bg-rose-100 text-rose-700': job.status === 'rejected',
          }"
        >
          {{ job.status }}
        </span>
      </div>

      <div class="grid grid-cols-2 gap-3">
        <div class="rounded-xl bg-[#e9f2f8] p-3">
          <p class="text-[10px] uppercase font-bold text-[#0d4169]/70">Salary</p>
          <p class="text-sm font-bold text-[#0d4169]">{{ job.salary }}</p>
        </div>
        <div class="rounded-xl bg-slate-50 border border-slate-100 p-3">
          <p class="text-[10px] uppercase font-bold text-slate-500">Sector</p>
          <p class="text-sm font-bold text-slate-800">{{ job.sector }}</p>
        </div>
        <div class="rounded-xl bg-slate-50 border border-slate-100 p-3">
          <p class="text-[10px] uppercase font-bold text-slate-500">Company Name</p>
          <p class="text-sm font-bold text-slate-800">{{ job.company_name || '—' }}</p>
        </div>
        <div class="rounded-xl bg-slate-50 border border-slate-100 p-3">
          <p class="text-[10px] uppercase font-bold text-slate-500">Work Location</p>
          <p class="text-sm font-bold text-slate-800">{{ job.work_location || '—' }}</p>
        </div>
      </div>

      <div>
        <p class="field-label mb-1">Description</p>
        <p class="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">{{ job.description }}</p>
      </div>

      <div class="text-xs text-slate-500 space-y-1">
        <p>Employer: {{ job.employer_name || '—' }}</p>
        <p v-if="job.phone">Phone: {{ job.phone }}</p>
        <p v-if="job.telegram_username">Telegram: @{{ job.telegram_username }}</p>
        <p>Submitted: {{ formatDate(job.created_at) }}</p>
        <p>Applications: {{ job.applications_count }}</p>
      </div>

      <div v-if="job.status === 'pending'" class="flex flex-wrap gap-2 pt-2">
        <button type="button" class="btn-success" :disabled="busy" @click="approve">
          {{ busy ? 'Working…' : 'Approve & Publish' }}
        </button>
        <button type="button" class="btn-danger" :disabled="busy" @click="reject">
          Reject
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { apiFetch } from '../utils/api'

export default {
  name: 'JobReviewView',
  setup() {
    const route = useRoute()
    const router = useRouter()
    const job = ref(null)
    const loading = ref(true)
    const error = ref('')
    const busy = ref(false)

    function formatDate(value) {
      return value ? new Date(value).toLocaleString() : ''
    }

    async function load() {
      loading.value = true
      error.value = ''
      try {
        const res = await apiFetch(`/jobs/${route.params.id}`)
        const data = await res.json().catch(() => ({}))
        if (!res.ok) throw new Error(data.message || 'Job not found')
        job.value = data.job
      } catch (err) {
        error.value = err.message
      } finally {
        loading.value = false
      }
    }

    async function approve() {
      busy.value = true
      try {
        const res = await apiFetch(`/jobs/${route.params.id}/approve`, { method: 'POST' })
        const data = await res.json().catch(() => ({}))
        if (!res.ok) throw new Error(data.message || 'Approve failed')
        job.value = data.job
        router.push({ name: 'jobs', query: { status: 'published' } })
      } catch (err) {
        alert(err.message)
      } finally {
        busy.value = false
      }
    }

    async function reject() {
      if (!confirm('Reject this job?')) return
      busy.value = true
      try {
        const res = await apiFetch(`/jobs/${route.params.id}/reject`, { method: 'POST' })
        const data = await res.json().catch(() => ({}))
        if (!res.ok) throw new Error(data.message || 'Reject failed')
        job.value = data.job
        router.push({ name: 'jobs', query: { status: 'rejected' } })
      } catch (err) {
        alert(err.message)
      } finally {
        busy.value = false
      }
    }

    onMounted(load)
    return { job, loading, error, busy, formatDate, approve, reject }
  },
}
</script>
